import { useState, useEffect } from "react";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import RankBadge from "./RankBadge";
import { fmtCurrency, fmtNumber, fmtDecimal } from "../lib/format";

function pct(v) {
  return v == null ? "—" : `${v.toFixed(1)}%`;
}

function useRowLimit() {
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 768 : true);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop ? 10 : 5;
}

// variant: "floor" | "category" | "salesman"
export default function PerfTable({ variant, rows, ranks, showAmount, onRowClick, activeName, testId, emptyMsg, nameLabel }) {
  const isCategory = variant === "category";
  const isSalesman = variant === "salesman";
  const limit = useRowLimit();
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? rows : rows.slice(0, limit);
  const hasMore = rows.length > limit;

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      {rows.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500">{emptyMsg}</div>
      ) : (
        <>
        <div className="thin-scroll overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2.5 text-left">{nameLabel}</th>
                {isSalesman && <th className="px-3 py-2.5 text-left">Floor</th>}
                {showAmount && <th className="px-3 py-2.5 text-right">Net Sales</th>}
                <th className="px-3 py-2.5 text-right">Net Qty</th>
                <th className="px-3 py-2.5 text-right">Bills</th>
                {!isCategory && <th className="px-3 py-2.5 text-right">ABV</th>}
                {!isCategory && <th className="px-3 py-2.5 text-right">ABQ</th>}
                {showAmount && <th className="px-3 py-2.5 text-right">Sales %</th>}
                <th className="px-3 py-2.5 text-right">Bills %</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const active = activeName === r.name;
                const dimmed = activeName && !active;
                return (
                <tr
                  key={r.name}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  data-testid={`row-${r.name}`}
                  className={`border-b border-slate-100 transition-colors ${onRowClick ? "cursor-pointer hover:bg-blue-50/70" : ""} ${active ? "bg-blue-50 ring-1 ring-inset ring-blue-300" : ""} ${dimmed ? "opacity-45" : ""}`}
                >
                  <td className="px-3 py-3 text-sm font-medium text-slate-800">
                    <span className={active ? "border-l-2 border-blue-600 pl-2" : ""}>{r.name}</span>
                  </td>
                  {isSalesman && <td className="px-3 py-3 text-sm text-slate-500">{r.floor || "—"}</td>}
                  {showAmount && (
                    <td className="whitespace-nowrap px-3 py-3 text-right font-mono-data text-sm text-slate-700">
                      {fmtCurrency(r.net_sales)}<RankBadge rank={ranks.net_sales?.[r.name]} />
                    </td>
                  )}
                  <td className="px-3 py-3 text-right font-mono-data text-sm text-slate-700">{fmtNumber(r.net_qty)}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono-data text-sm text-slate-700">
                    {fmtNumber(r.bills)}<RankBadge rank={ranks.bills?.[r.name]} />
                  </td>
                  {!isCategory && (
                    <td className="whitespace-nowrap px-3 py-3 text-right font-mono-data text-sm font-semibold text-slate-900">
                      {fmtCurrency(r.abv, true)}<RankBadge rank={ranks.abv?.[r.name]} />
                    </td>
                  )}
                  {!isCategory && (
                    <td className="whitespace-nowrap px-3 py-3 text-right font-mono-data text-sm font-semibold text-slate-900">
                      {fmtDecimal(r.abq)}<RankBadge rank={ranks.abq?.[r.name]} />
                    </td>
                  )}
                  {showAmount && <td className="px-3 py-3 text-right font-mono-data text-xs text-slate-500">{pct(r.net_sales_share)}</td>}
                  <td className="px-3 py-3 text-right font-mono-data text-xs text-slate-500">{pct(r.bills_share)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <button
            onClick={() => setExpanded((e) => !e)}
            data-testid={`${testId}-toggle`}
            className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-50/60"
          >
            {expanded ? (<><CaretUp size={15} weight="bold" /> Show less</>) : (<><CaretDown size={15} weight="bold" /> View all ({rows.length})</>)}
          </button>
        )}
        </>
      )}
    </div>
  );
}
